import React from 'react';
import autoBind from 'react-autobind';
import { Layout, PageHeader, Table, Button, Checkbox } from 'antd';
import * as ExcelJS from 'exceljs';
//
import CommonLoadingView from '../../commonComponents/CommonLoadingView';
import CustomComponent from '../../../components/CustomComponent';
import DateRangeFilter from '../../../components/DateRangeFilter';
import { DownloadOutlined } from '@ant-design/icons';
//
import Utils from '../../../components/Utils';
import Globals from '../../../config/Globals';
//
export default class AdminRefundPerPeriodReportView extends CustomComponent {
  constructor(props) {
    super(props);
    autoBind(this);
    this.state = {
      isLoading: false,
      refunds: [],
      reportDates: [],
      onlyUserInitiated: false,
      firstLoad: true,
      sortedInfo: null,
    };
  }

  async handleFilter() {
    const [from, to] = this.state.reportDates || [];
    if (!from || !to) {
      this.setState({ refunds: [] });
      return;
    }

    this.setState({ isLoading: true, refunds: [] });

    const filters = {
      dateFrom: from.valueOf(),
      dateTo: to.valueOf(),
      ...(this.state.onlyUserInitiated && { isUserInitiated: true }),
    };

    const resp = await this.props.app.license.order.searchOrderRefunds({ filters });

    if (!this._isMounted) return;
    if (resp.statusCode === 200) {
      this.setState({ refunds: resp.body.refunds, isLoading: false, firstLoad: false });
    } else {
      this.props.app.alertController.showAPIErrorAlert(null, resp);
      this.setState({ isLoading: false, firstLoad: false });
    }
  }

  async exportToXLSX() {
    const [from, to] = this.state.reportDates || [];
    if (!from || !to) return;

    const filters = {
      dateFrom: from.valueOf(),
      dateTo: to.valueOf(),
      ...(this.state.onlyUserInitiated && { isUserInitiated: true }),
    };

    this.startLoading();
    //Make request
    const resp = await this.props.app.license.order.exportOrderRefunds({ filters });
    if (resp.statusCode == 200) {
      Utils.downloadBlob(resp.body, 'OrderRefunds', 'xlsx');
    } else {
      this.props.app.alertController.showAPIErrorAlert(null, resp);
    }
    this.stopLoading();
  }

  handleFilterChange(pagination, filters, sorter) {
    this.setState({ sortedInfo: sorter });
  }

  render() {
    let { sortedInfo } = this.state;
    sortedInfo = sortedInfo || {};
    const columns = [
      {
        title: 'Order',
        dataIndex: 'id',
        key: 'id',
        sorter: (a, b) => a.id.localeCompare(b.id),
        sortOrder: sortedInfo.columnKey === 'id' && sortedInfo.order,
      },
      {
        title: 'Date',
        dataIndex: 'refundedOn',
        key: 'refundedOn',
        width: '15%',
        render: (val) => (val ? Utils.getDateOnUIFormatByTimestamp(val) : ''),
        sorter: (a, b) => a.refundedOn - b.refundedOn,
        sortOrder: sortedInfo.columnKey === 'refundedOn' && sortedInfo.order,
      },
      {
        title: 'License',
        dataIndex: 'licenseID',
        key: 'licenseID',
        sorter: (a, b) => a.licenseID.localeCompare(b.licenseID),
        sortOrder: sortedInfo.columnKey === 'licenseID' && sortedInfo.order,
      },
      {
        title: 'Product',
        dataIndex: 'productName',
        key: 'productName',
        sorter: (a, b) => a.productName.localeCompare(b.productName),
        sortOrder: sortedInfo.columnKey === 'productName' && sortedInfo.order,
      },
      {
        title: 'Refunded to',
        key: 'refundedTo',
        render: (record) => {
          return record.name || record.email || '-';
        },
        sorter: (a, b) => {
          const aName = a.name || a.email || '';
          const bName = b.name || b.email || '';
          return aName.localeCompare(bName);
        },
      },
      {
        title: 'Requested by',
        dataIndex: 'isUserInitiated',
        key: 'isUserInitiated',
        render: (val) => (val ? 'User' : 'Administrator'),
        sorter: (a, b) => {
          const aName = a.isUserInitiated ? 'User' : 'Administrator';
          const bName = b.isUserInitiated ? 'User' : 'Administrator';
          return aName.localeCompare(bName);
        },
      },
      {
        title: 'Transaction',
        dataIndex: 'providerTransactionID',
        key: 'providerTransactionID',
        sorter: (a, b) => a.providerTransactionID.localeCompare(b.providerTransactionID),
        sortOrder: sortedInfo.columnKey === 'providerTransactionID' && sortedInfo.order,
      },
      {
        title: 'Amount',
        dataIndex: 'refundAmount',
        key: 'refundAmount',
        align: 'right',
        render: (val) => `$${Utils.toCurrencyFormat(val)}`,
        sorter: (a, b) => a.refundAmount - b.refundAmount,
        sortOrder: sortedInfo.columnKey === 'refundAmount' && sortedInfo.order,
      },
      {
        title: 'Taxes',
        dataIndex: 'refundTax',
        key: 'refundTax',
        align: 'right',
        render: (val) => `$${Utils.toCurrencyFormat(val)}`,
        sorter: (a, b) => a.refundTax - b.refundTax,
        sortOrder: sortedInfo.columnKey === 'refundTax' && sortedInfo.order,
      },
      {
        title: 'Total',
        dataIndex: 'refundTotal',
        key: 'refundTotal',
        align: 'right',
        render: (val) => `$${Utils.toCurrencyFormat(val)}`,
        sorter: (a, b) => a.refundTotal - b.refundTotal,
        sortOrder: sortedInfo.columnKey === 'refundTotal' && sortedInfo.order,
      },
    ];
    return (
      <Layout.Content className="pageContent">
        <CommonLoadingView isLoading={this.state.isLoading} />
        <PageHeader className="pageHeader" title="Refund per Period" />
        <Layout.Content>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <DateRangeFilter
              buttonLabel="Run Report"
              onChange={(dates) => this.setState({ reportDates: dates })}
              onFilter={this.handleFilter}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Checkbox
                checked={this.state.onlyUserInitiated}
                onChange={(e) => this.setState({ onlyUserInitiated: e.target.checked })}
              >
                Show only user initiated refunds
              </Checkbox>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={this.exportToXLSX}
                disabled={this.state.refunds.length < 1}
              >
                Export to xlsx
              </Button>
            </div>
          </div>
          <Table
            rowKey={Math.random()}
            className="adminSearchUsersTable"
            loading={this.state.isLoading}
            columns={columns}
            dataSource={this.state.refunds}
            onChange={this.handleFilterChange}
            locale={{ emptyText: this.state.firstLoad ? 'Search refunds' : 'No refunds found!' }}
            pagination={{
              pageSize: Globals.Table_PagingItemsPerPage,
              hideOnSinglePage: true,
              showSizeChanger: false,
              position: ['bottomCenter'],
            }}
          />
        </Layout.Content>
      </Layout.Content>
    );
  }
}
